import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Shield, Compass, Building2, Hammer, Layers, Briefcase, Users, HandCoins, Wrench } from "lucide-react";
import { Section, Overline, H1, H2, Lead, Card } from "@/components/ui-kit";
import { api } from "@/lib/api";

const HERO_BG = "https://images.unsplash.com/photo-1721244654394-36a7bc2da288?auto=format&fit=crop&w=1900&q=80";

export default function Home() {
  const [featured, setFeatured] = useState(null);
  useEffect(() => {
    api.get("/featured-case-study").then((r) => setFeatured(r.data?.card || null)).catch(() => setFeatured(null));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 hero-vignette" />
          <div className="absolute inset-0 bp-grid" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 lg:px-12 pt-20 md:pt-28 pb-10 sm:pb-24 md:pb-32">
          <Overline>Brightside Capital Ventures · Roseville/Sacramento, California</Overline>
          <H1 className="mt-5 max-w-4xl">Creative real estate solutions for owners, investors, and buyers.</H1>
          <Lead className="mt-7 max-w-2xl">
            We help property owners explore flexible sale options, connect investors with disciplined real estate
            opportunities, and build relationships with serious cash buyers across Sacramento and Northern California.
          </Lead>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/sell" className="btn-gold px-7 py-4 font-semibold tracking-wide" data-testid="hero-cta-sell">Sell Your Property</Link>
            <Link to="/investors" className="btn-ghost px-7 py-4 font-semibold tracking-wide" data-testid="hero-cta-invest">Join Investor List</Link>
            <Link to="/buyers" className="btn-ghost px-7 py-4 font-semibold tracking-wide" data-testid="hero-cta-buyer">Join Buyer List</Link>
          </div>
          <div className="mt-14 border-t border-white/10 pt-6 flex flex-col items-center text-center gap-y-2.5 sm:flex-row sm:flex-wrap sm:items-stretch sm:text-left sm:gap-x-10 sm:gap-y-3 text-xs tracking-widest uppercase text-neutral-400" data-testid="hero-descriptors">
            <span>Veteran-Led</span>
            <span className="hidden sm:inline text-[#C19B6C]">·</span>
            <span>Sacramento-Focused</span>
            <span className="hidden sm:inline text-[#C19B6C]">·</span>
            <span>Small Multifamily & Value-Add</span>
            <span className="hidden sm:inline text-[#C19B6C]">·</span>
            <span>Creative Solutions</span>
          </div>
        </div>
      </section>

      {/* WHO WE HELP */}
      <Section className="pt-0 pb-24 sm:py-24 md:py-32" dataTestId="who-we-help">
        <Overline>01 · Who We Help</Overline>
        <H2 className="mt-3 max-w-3xl">Four conversations. One disciplined operator.</H2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Building2, title: "Property Owners", body: "Selling a tired rental, distressed property, inherited home, or small multifamily? We evaluate practical exit options." },
            { icon: HandCoins, title: "Passive Investors", body: "Private lending, JV equity, and project-specific opportunities backed by real estate and conservative underwriting." },
            { icon: Briefcase, title: "Cash Buyers", body: "Off-market and value-add inventory: SFH, 2-4, small multifamily, ADU, and infill plays in our buy box." },
            { icon: Users, title: "Strategic Partners", body: "Agents, contractors, lenders, attorneys, title, and PMs who want consistent, professional deal flow." },
          ].map((c) => (
            <Card key={c.title}>
              <c.icon className="text-[#C19B6C]" size={26} strokeWidth={1.4} />
              <h3 className="font-display text-xl mt-6">{c.title}</h3>
              <p className="text-sm text-neutral-400 mt-3 leading-relaxed">{c.body}</p>
            </Card>
          ))}
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <section className="bg-[#0A0A0A] border-y border-white/10 relative">
        <div className="absolute inset-0 bp-grid-fine opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-24 md:py-32">
          <Overline>02 · How It Works</Overline>
          <H2 className="mt-3 max-w-3xl">Simple, direct, and built around your situation.</H2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { n: "01", t: "Tell us the basics", b: "Property, situation, goals. Only what's needed to evaluate." },
              { n: "02", t: "We review directly", b: "An operator looks at condition, location, timeline, and structure options." },
              { n: "03", t: "We follow up", b: "A real conversation about practical paths forward, never a pitch." },
              { n: "04", t: "Decide on your terms", b: "If there's a fit, we structure it around your goals. If not, we'll say so." },
            ].map((s) => (
              <Card key={s.n}>
                <div className="font-display text-3xl text-[#C19B6C]">{s.n}</div>
                <h3 className="font-display text-lg mt-5">{s.t}</h3>
                <p className="text-sm text-neutral-400 mt-2 leading-relaxed">{s.b}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FOCUS */}
      <section className="bg-[#0A0A0A] border-y border-white/10 relative">
        <div className="absolute inset-0 bp-grid-fine opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-24 md:py-32">
          <Overline>03 · What We Focus On</Overline>
          <H2 className="mt-3 max-w-3xl">Asset classes and strategies we underwrite every week.</H2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Building2, t: "Single-family with upside", b: "Distressed, deferred maintenance, or repositionable parcels." },
              { icon: Layers, t: "2-4 unit properties", b: "Small multi with operational and capex levers." },
              { icon: Briefcase, t: "5-15 unit apartments", b: "Workforce housing positioned for NOI growth." },
              { icon: Hammer, t: "ADU / garage conversions", b: "Code-compliant infill that adds an income unit." },
              { icon: Wrench, t: "Value-add renovations", b: "Scoped to comps. Underwritten conservatively." },
              { icon: Compass, t: "Creative finance", b: "Seller carry, sub-to (where appropriate), JV, hybrid offers." },
            ].map((f) => (
              <Card key={f.t}>
                <f.icon className="text-[#C19B6C]" size={24} strokeWidth={1.4} />
                <h3 className="font-display text-lg mt-5">{f.t}</h3>
                <p className="text-sm text-neutral-400 mt-2 leading-relaxed">{f.b}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <Section className="py-24 md:py-32" dataTestId="why-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Overline>04 · Why Brightside</Overline>
            <H2 className="mt-3">Operator mindset. Investor discipline. Local focus.</H2>
            <p className="text-neutral-400 mt-6 leading-relaxed">
              We don't pretend to be the highest cash bid on every property. We win when our structure, basis, and
              follow-through align with what the seller, the asset, and the investor capital actually need.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              { t: "Conservative underwriting", b: "Reserves, comps, and exit assumptions stress-tested." },
              { t: "Multiple exit strategies", b: "Refinance, hold, or sale, known before we close." },
              { t: "Transparent communication", b: "Plain language. Clear scope. Real timelines." },
              { t: "Respectful seller process", b: "No pressure. Practical options matched to your goals." },
            ].map((w) => (
              <div key={w.t} className="p-6 border-l border-[#C19B6C]/60">
                <Shield className="text-[#C19B6C]" size={18} strokeWidth={1.4} />
                <h4 className="font-display text-lg mt-4">{w.t}</h4>
                <p className="text-sm text-neutral-400 mt-2">{w.b}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* FEATURED CASE STUDY (owner-selected; hidden until a published case study is featured) */}
      {featured && (
        <section className="relative" data-testid="featured-case-study">
          <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-16 md:py-28">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="relative aspect-[4/3] overflow-hidden border border-white/10">
                {featured.image && <img src={featured.image} alt={featured.title} className="w-full h-full object-cover" />}
                <div className="absolute inset-0 bp-grid-fine opacity-40 pointer-events-none" />
              </div>
              <div>
                <Overline>Featured Case Study{featured.project_status && featured.project_status !== "completed" ? ` · ${featured.project_status === "in_progress" ? "In progress" : "Planned"}` : ""}</Overline>
                <H2 className="mt-3" data-testid="featured-title">{featured.title}</H2>
                <p className="text-neutral-400 mt-5 leading-relaxed" data-testid="featured-description">{featured.description}</p>
                <Link to="/deals" className="inline-flex items-center gap-2 mt-8 text-[#C19B6C] hover:text-white transition-colors group" data-testid="featured-cta">
                  View Case Studies <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="border-t border-white/10 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-20 md:py-28">
          <H2 className="max-w-3xl">Have a property, capital, or buyer criteria? Start here.</H2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link to="/sell" className="btn-gold py-5 px-6 text-center font-semibold">Sell a Property</Link>
            <Link to="/investors" className="btn-ghost py-5 px-6 text-center font-semibold">Investor Interest Form</Link>
            <Link to="/buyers" className="btn-ghost py-5 px-6 text-center font-semibold">Buyer Intake Form</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
